import { Position } from 'geojson';

import { PlotReportFactElement } from './fact';
// eslint-disable-next-line import/no-cycle
import { Organization } from './organization';

// TODO: (MVP-1132): these types should be generated automatically based on the API's Swagger documentation

// eslint-disable-next-line @typescript-eslint/ban-types
export type LandQuestionnaireAnswers<T = {}> = Partial<T>;

type Point = {
  lat: number;
  lon: number;
};

// TODO: MVP-9664 - remove transformMultiPolygonToPolygon when BE drops required polygon for plot POST request
export type Polygon = {
  type: 'Polygon';
  coordinates: Position[][];
};

export type MultiPolygon = {
  type: 'MultiPolygon';
  coordinates: Position[][][];
};

export enum PlotType {
  GRASSLAND = 'grassland',
  CROPLAND = 'cropland',
  FORESTRY = 'forestry',
  TREE_COVER = 'tree_cover',
  HERBACEOUS_WETLAND = 'herbaceous_wetland',
  MOSS_AND_LICHENS = 'moss_and_lichens',
  BUILT_UP = 'built_up',
  SHRUBLAND = 'shrubland',
  BARE_AND_SPARSE_VEGETATION = 'bare_and_sparse_vegetation',
  MANGROVES = 'mangroves',
  PERMANENT_WATER_BODIES = 'permanent_water_bodies',
  SNOW_AND_ICE = 'snow_and_ice',
  UNDEFINED = 'undefined',
  MIXED = 'mixed',
  COASTAL_MARINE = 'coastal_marine',
}

export enum GrasslandGrassType {
  NONE = 'NONE',
  PERENNIAL_GRASS = 'PERENNIAL_GRASS',
  GRASS_CLOVER_MIXTURE = 'GRASS_CLOVER_MIXTURE',
  ALFALFA = 'ALFALFA',
}

export enum GrasslandOrganicFertilizerType {
  NONE = 'NONE',
  SLURRY = 'SLURRY',
  FORCED_AERIATION_COMPOST_OR_MANURE = 'FORCED_AERIATION_COMPOST_OR_MANURE',
  NON_FORCED_AERIATION_COMPOST_OR_MANURE = 'NON_FORCED_AERIATION_COMPOST_OR_MANURE',
}

export enum CropsEnum {
  alfalfa = 'alfalfa',
  apple = 'apple',
  barley = 'barley',
  canola = 'canola',
  clover = 'clover',
  coffee = 'coffee',
  cotton = 'cotton',
  dry_bean = 'dry_bean',
  grass_clover_mix = 'grass_clover_mix',
  hops = 'hops',
  maize_grain = 'maize_grain',
  maize_silage = 'maize_silage',
  millet = 'millet',
  oats = 'oats',
  peanut = 'peanut',
  perennial_grass = 'perennial_grass',
  potato = 'potato',
  rapeseed = 'rapeseed',
  rice = 'rice',
  rye = 'rye',
  sorghum = 'sorghum',
  soyabean = 'soyabean',
  spring_wheat = 'spring_wheat',
  tea = 'tea',
  tomato = 'tomato',
  tree_crop = 'tree_crop',
  vegetable_above_ground = 'vegetable_above_ground',
  sugarcane = 'sugarcane',
  vegetable_root = 'vegetable_root',
  winter_wheat = 'winter_wheat',
  other = 'other',
  other_grain = 'other_grain',
  other_legume = 'other_legume',
  other_n_fixing_forage = 'other_n_fixing_forage',
  other_non_n_fixing_forage = 'other_non_n_fixing_forage',
  other_root_crops = 'other_root_crops',
  other_tuber_crop = 'other_tuber_crop',
  blueberry = 'blueberry',
  raspberry = 'raspberry',
  strawberry = 'strawberry',
}

export enum ErrorsEnum {
  self_intersecting = 'self_intersecting',
  too_small = 'too_small',
  too_large = 'too_large',
  invalid_shape_index = 'invalid_shape_index',
  contains_holes = 'contains_holes',
}

export enum PlotStatusEnum {
  invalid = 'invalid',
  new_plot = 'new_plot',
  draft = 'draft',
  ready_to_analyse = 'ready_to_analyse',
  scheduled_for_analysis = 'scheduled_for_analysis',
  analysed = 'analysed',
  failed = 'failed',
}

export enum LandManagementProcessEnum {
  agriculture = 'agriculture',
  conservation = 'conservation',
  unmanaged = 'unmanaged',
}

export enum PlotOwnershipEnum {
  in_value_chain = 'in_value_chain',
  operator_owner = 'operator_owner',
  investor_lender = 'investor_lender',
  insurer = 'insurer',
  general_screening = 'general_screening',
}

export type DraftPlot = {
  id: string;
  user_id: string;
  name: string;
  land_type: PlotType;
  multi_polygon: MultiPolygon | null;
  area: number;
  project_id: string | null;
  crops: CropsEnum[];
  land_management_process: LandManagementProcessEnum;
  ownership?: PlotOwnershipEnum | '';
  land_steward_name: string;
  label_ids: string[];
  errors: ErrorsEnum[];
};

export type Labels = {
  label_name_id: string;
  label_value_id: string;
  name: string;
  value: string;
};

export type Plot = {
  id: string;
  name: string;
  type: PlotType;
  multi_polygon: MultiPolygon;
  area: number;
  project?: string | null;
  project_id?: string | null; // TODO: MVP-6054 remove these inconsistences: BE is inconsistent in how they send the project id. E.g.: project_id is used by /api/v1/plots
  organization: Organization;
  crops: CropsEnum[];
  errors: ErrorsEnum[];
  has_complete_questionnaire: boolean;
  status: PlotStatusEnum;
  created_at: string;
  modified_at: string;
  last_analyzed_at: string | null;
  approximation_method: string;
  address: string;
  biome?: string | null;
  ecoregions?: string | null;
  labels?: Labels[];
  // True only for synthetic label-aggregate rows returned by the org plot list
  // endpoint when grouping by label. Real plots (including project-less
  // org-level plots) always have this as false/undefined.
  is_group?: boolean;
};

export const PLOT_MEDIA_FILE_TYPE = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
} as const;

export const PLOT_MEDIA_FILE_ALLOWED_FORMATS = {
  [PLOT_MEDIA_FILE_TYPE.IMAGE]: ['png', 'jpeg', 'jpg'],
  [PLOT_MEDIA_FILE_TYPE.VIDEO]: ['mp4', 'webm'],
  [PLOT_MEDIA_FILE_TYPE.AUDIO]: ['mp3', 'wav'],
};

export type PlotOverviewMediaFileType = (typeof PLOT_MEDIA_FILE_TYPE)[keyof typeof PLOT_MEDIA_FILE_TYPE];

export type PlotOverviewMediaFile = {
  id: string;
  caption: string;
  name: string;
  file_info_id: string;
  url: string;
  type: PlotOverviewMediaFileType;
  thumbnail_url: string | null;
  thumbnail_file_info_id: string | null;
  is_approved: boolean;
};
export type PlotOverview = {
  id: string;
  title: string;
  description: string;
  plot: string;
  is_approved: boolean;
  media_files: PlotOverviewMediaFile[];
};

export type PlotWithFacts = Plot & {
  facts: PlotReportFactElement[];
};
/**
 * GET /api/v1/draftplot
 */
export const GET_DRAFT_PLOT_PATH = 'draftplot';
export type GetDraftPlotRequestPathVariables = undefined;

export type GetDraftPlotRequestQueryParams =
  | {
      project?: string;
      organization?: string;
      land_type?: string;
      limit?: number;
      offset?: number;
      // Staff-only: widens results to all drafts for the organization, not just the requester's own uploads.
      all_org_drafts?: boolean;
    }
  | undefined;
export type GetDraftPlotSuccessDto = {
  count: number;
  next: string | null;
  previous: string | null;
  results: DraftPlot[];
};
export type GetDraftPlotErrorDto = Error;

/**
 * POST /api/v1/draftplot
 */
export const POST_DRAFT_PLOT_PATH = 'draftplot';
export type PostDraftPlotRequestPathVariables = undefined;
export type PostDraftPlotRequestQueryParams = undefined;
export type PostDraftPlotRequestBodyData = {
  file: File;
  land_type: PlotType;
  project?: string;
  organization?: string;
  auto_fix_holes?: boolean;
  land_steward_name_column?: string;
  label_columns?: string[];
};
export type PostDraftPlotSuccessDto = {
  fixed_polygons?: number;
  total_polygons?: number;
};
export type PostDraftPlotErrorDto = Error;

/**
 * POST /api/v1/draftplot/commit
 */
export const POST_DRAFT_PLOT_COMMIT_PATH = 'draftplot/commit/';
export type PostDraftPlotCommitRequestPathVariables = undefined;
export type PostDraftPlotCommitRequestQueryParams = undefined;
export type PostDraftPlotCommitRequestBodyData = {
  draft_plots: string[];
};
export type PostDraftPlotCommitSuccessDto = undefined;
export type PostDraftPlotCommitErrorDto = Error;

/**
 * POST /api/v1/draftplot/commit_async/
 */
export const POST_DRAFT_PLOT_COMMIT_ASYNC_PATH = 'draftplot/commit_async/';
export type PostDraftPlotCommitAsyncRequestPathVariables = undefined;
export type PostDraftPlotCommitAsyncRequestQueryParams = undefined;
export type PostDraftPlotCommitAsyncRequestBodyData = {
  draft_plots: string[];
};
export type PostDraftPlotCommitAsyncSuccessDto = { task_id: string };
export type PostDraftPlotCommitAsyncErrorDto = Error;

/**
 * POST /api/v1/draftplot/validate-names/
 */
export const POST_DRAFT_PLOT_VALIDATE_NAMES_PATH = 'draftplot/validate_names/';
export type PostDraftPlotValidateNamesRequestPathVariables = undefined;
export type PostDraftPlotValidateNamesRequestQueryParams = undefined;
export type PostDraftPlotValidateNamesRequestBodyData = {
  draft_plots: string[];
};
export enum DraftPlotNameConflictReasonEnum {
  already_exists = 'already_exists',
  duplicate_in_selection = 'duplicate_in_selection',
}

export type DraftPlotNameConflict = {
  id: string;
  name: string;
  reason: DraftPlotNameConflictReasonEnum;
};
export type PostDraftPlotValidateNamesSuccessDto = { conflicts: DraftPlotNameConflict[] };
export type PostDraftPlotValidateNamesErrorDto = Error;

/**
 * POST /api/v1/draftplot/from_coordinates/
 */
export const POST_DRAFT_PLOT_FROM_COORDINATES_PATH = 'draftplot/from_coordinates/';
export type PostDraftPlotFromCoordinatesRequestPathVariables = undefined;
export type PostDraftPlotFromCoordinatesRequestQueryParams = undefined;
export type PostDraftPlotFromCoordinatesRequestBodyData = {
  name?: string;
  latitude: number;
  longitude: number;
  land_type: PlotType;
  project?: string;
  organization?: string;
};
export type PostDraftPlotFromCoordinatesSuccessDto = DraftPlot;
export type PostDraftPlotFromCoordinatesErrorDto = Error;

/**
 * POST /api/v1/draftplot/analyze_spreadsheet/
 */
export const POST_DRAFT_PLOT_ANALYZE_SPREADSHEET_PATH = 'draftplot/analyze_spreadsheet/';
export type PostDraftPlotAnalyzeSpreadsheetRequestPathVariables = undefined;
export type PostDraftPlotAnalyzeSpreadsheetRequestQueryParams = undefined;
export type PostDraftPlotAnalyzeSpreadsheetRequestBodyData = { file: File };
export type PostDraftPlotAnalyzeSpreadsheetSuccessDto = {
  file_id: string;
  sheet_names: string[];
  columns_by_sheet: Record<string, string[]>;
};
export type PostDraftPlotAnalyzeSpreadsheetErrorDto = Error;

/**
 * POST /api/v1/draftplot/from_spreadsheet/
 */
export const POST_DRAFT_PLOT_FROM_SPREADSHEET_PATH = 'draftplot/from_spreadsheet/';
export type PostDraftPlotFromSpreadsheetRequestPathVariables = undefined;
export type PostDraftPlotFromSpreadsheetRequestQueryParams = undefined;
export type PostDraftPlotFromSpreadsheetRequestBodyData = {
  file_id: string;
  sheet_name?: string;
  latitude_label: string;
  longitude_label: string;
  area_label?: string;
  area_units?: 'square_meters' | 'hectares' | 'acres' | 'square_kilometers';
  name_label?: string;
  land_type: PlotType;
  project?: string;
  organization?: string;
};
export type PostDraftPlotFromSpreadsheetSuccessDto = {
  task_id: string;
};
export type PostDraftPlotFromSpreadsheetErrorDto = Error;

/**
 * GET /api/v1/draftplot/task-status/
 */
export const GET_DRAFT_PLOT_TASK_STATUS_PATH = 'draftplot/task-status/';
export type GetDraftPlotTaskStatusRequestPathVariables = undefined;
export type GetDraftPlotTaskStatusRequestQueryParams = { task_id: string };
export type DraftPlotUploadTaskStatus = 'PENDING' | 'SUCCESS' | 'FAILED';
export type GetDraftPlotTaskStatusSuccessDto = {
  task_id: string;
  status: DraftPlotUploadTaskStatus;
  plots_created: number;
  plots_retained_as_draft: number;
  error_message: string | null;
};
export type GetDraftPlotTaskStatusErrorDto = Error;

/**
 * DELETE /api/v1/draftplot/{id}/
 */
export const DELETE_DRAFT_PLOT_PATH = 'draftplot/{id}';
export type DeleteDraftPlotRequestPathVariables = { id: string };
export type DeleteDraftPlotRequestQueryParams = undefined;
export type DeleteDraftPlotSuccessDto = undefined;
export type DeleteDraftPlotErrorDto = Error;

/**
 * DELETE /api/v1/draftplot/discard/
 */
export const DELETE_DRAFT_PLOT_DISCARD_PATH = 'draftplot/discard/';
export type DeleteDraftPlotDiscardRequestPathVariables = undefined;
export type DeleteDraftPlotDiscardRequestQueryParams = { organization?: string };
export type DeleteDraftPlotDiscardSuccessDto = { deleted: number };
export type DeleteDraftPlotDiscardErrorDto = Error;

/**
 * PATCH /api/v1/draftplot/{id}/
 */
export const PATCH_DRAFT_PLOT_PATH = 'draftplot/{id}';
export type PatchDraftPlotRequestPathVariables = { id: string };
export type PatchDraftPlotRequestQueryParams = undefined;
export type PatchDraftPlotRequestBodyData = Partial<{
  name: string;
  land_type: PlotType;
  polygon: Polygon;
  multi_polygon: MultiPolygon;
  crops: CropsEnum[];
  land_management_process: LandManagementProcessEnum;
  ownership: PlotOwnershipEnum | '';
  land_steward_name: string;
  label_ids: string[];
}>;
export type PatchDraftPlotSuccessDto = DraftPlot;
export type PatchDraftPlotErrorDto = Error;

/**
 * PATCH /api/v1/draftplot/bulk_patch/
 */
export const PATCH_DRAFT_PLOT_BULK_PATH = 'draftplot/bulk_patch/';
export type PatchDraftPlotBulkRequestPathVariables = undefined;
export type PatchDraftPlotBulkRequestQueryParams = undefined;
export type PatchDraftPlotBulkRequestBodyData = {
  ids: string[];
} & Partial<{
  land_type: PlotType;
  crops: CropsEnum[];
  land_management_process: LandManagementProcessEnum;
  ownership: PlotOwnershipEnum | '';
}>;
export type PatchDraftPlotBulkSuccessDto = DraftPlot[];
export type PatchDraftPlotBulkErrorDto = Error;

/**
 * PATCH /api/v1/draftplot/bulk_assign/
 */
export const PATCH_DRAFT_PLOT_BULK_ASSIGN_PATH = 'draftplot/bulk_assign/';
export type PatchDraftPlotBulkAssignRequestPathVariables = undefined;
export type PatchDraftPlotBulkAssignRequestQueryParams = undefined;
export type PatchDraftPlotBulkAssignRequestBodyData = {
  ids: string[];
} & Partial<{
  label_ids: string[];
  draft_plot_group_id: string;
}>;
export type PatchDraftPlotBulkAssignSuccessDto = DraftPlot[];
export type PatchDraftPlotBulkAssignErrorDto = Error;

/**
 * DELETE /api/v1/draftplot/bulk_delete/
 */
export const DELETE_DRAFT_PLOT_BULK_DELETE_PATH = 'draftplot/bulk_delete/';
export type DeleteDraftPlotBulkDeleteRequestPathVariables = undefined;
export type DeleteDraftPlotBulkDeleteRequestQueryParams = { ids: string };
export type DeleteDraftPlotBulkDeleteSuccessDto = { deleted: number };
export type DeleteDraftPlotBulkDeleteErrorDto = Error;

/**
 * GET /api/v1/draftplotgroup
 */
export type DraftPlotGroupStatus = 'DRAFT' | 'READY';
export type DraftPlotGroup = {
  id: string;
  name: string;
  organization: string | null;
  project: string | null;
  land_steward_name: string;
  status: DraftPlotGroupStatus;
  draft_plot_ids: string[];
  total_plots: number;
  total_area: number;
};
export const GET_DRAFT_PLOT_GROUP_PATH = 'draftplotgroup';
export type GetDraftPlotGroupRequestPathVariables = undefined;
export type GetDraftPlotGroupRequestQueryParams = {
  project?: string;
  organization?: string;
  limit?: number;
  offset?: number;
};
export type GetDraftPlotGroupSuccessDto = {
  count: number;
  next: string | null;
  previous: string | null;
  results: DraftPlotGroup[];
};
export type GetDraftPlotGroupErrorDto = Error;

/**
 * POST /api/v1/draftplotgroup/
 */
export const POST_DRAFT_PLOT_GROUP_PATH = 'draftplotgroup';
export type PostDraftPlotGroupRequestPathVariables = undefined;
export type PostDraftPlotGroupRequestQueryParams = undefined;
export type PostDraftPlotGroupRequestBodyData = {
  name: string;
  organization?: string;
  project?: string;
} & Partial<{
  land_steward_name: string;
  status: DraftPlotGroupStatus;
  draft_plot_ids: string[];
}>;
export type PostDraftPlotGroupSuccessDto = DraftPlotGroup;
export type PostDraftPlotGroupErrorDto = Error;

/**
 * PATCH /api/v1/draftplotgroup/{id}/
 */
export const PATCH_DRAFT_PLOT_GROUP_PATH = 'draftplotgroup/{id}';
export type PatchDraftPlotGroupRequestPathVariables = { id: string };
export type PatchDraftPlotGroupRequestQueryParams = undefined;
export type PatchDraftPlotGroupRequestBodyData = Partial<{
  name: string;
  organization: string;
  project: string;
  land_steward_name: string;
  status: DraftPlotGroupStatus;
  draft_plot_ids: string[];
}>;
export type PatchDraftPlotGroupSuccessDto = DraftPlotGroup;
export type PatchDraftPlotGroupErrorDto = Error;

/**
 * DELETE /api/v1/draftplotgroup/{id}/
 */
export const DELETE_DRAFT_PLOT_GROUP_PATH = 'draftplotgroup/{id}';
export type DeleteDraftPlotGroupRequestPathVariables = { id: string };
export type DeleteDraftPlotGroupRequestQueryParams = undefined;
export type DeleteDraftPlotGroupSuccessDto = undefined;
export type DeleteDraftPlotGroupErrorDto = Error;

/**
 * GET /api/v1/plot/aggregate
 */
export const GET_PLOT_AGGREGATE_PATH = 'plot/aggregate';
export type GetPlotAggregateRequestPathVariables = undefined;
export type GetPlotAggregateRequestQueryParams =
  | {
      project?: string;
    }
  | undefined;
export type GetPlotAggregateSuccessDto = {
  plots_requiring_attention: number;
};
export type GetPlotAggregateErrorDto = Error;

/**
 * GET /api/v1/buyer/plot/aggregate
 */
export const GET_BUYER_PLOT_AGGREGATE_PATH = 'buyer/plot/aggregate';
export type GetBuyerPlotAggregateRequestPathVariables = undefined;
export type GetBuyerPlotAggregateRequestQueryParams =
  | {
      project?: string;
    }
  | undefined;
export type GetBuyerPlotAggregateSuccessDto = {
  plots_requiring_attention: number;
};
export type GetBuyerPlotAggregateErrorDto = Error;

/**
 * GET /api/v1/plot
 */
export const GET_PLOT_PATH = 'plot';
export type GetPlotRequestPathVariables = undefined;

export type GetPlotRequestQueryParams =
  | {
      project?: string;
      demo?: boolean;
    }
  | undefined;
export type GetPlotSuccessDto = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Plot[];
};
export type GetPlotErrorDto = Error;

/**
 * GET /api/v1/plot/{id}
 */
export const GET_PLOT_BY_ID_PATH = 'plot/{id}';
export type GetPlotByIdRequestPathVariables = { id: string };
export type GetPlotByIdRequestQueryParams = undefined;
export type GetPlotByIdSuccessDto = Plot;
export type GetPlotByIdErrorDto = Error;

/**
 * POST /api/v1/plot
 */
export const POST_PLOT_PATH = 'plot';
export type PostPlotRequestPathVariables = undefined;
export type PostPlotRequestQueryParams = undefined;
export type PostPlotRequestBodyData = {
  name: string;
  type: PlotType;
  multi_polygon: MultiPolygon;
  polygon: Polygon; // TODO: MVP-9664 - remove transformMultiPolygonToPolygon when BE drops required polygon for plot POST request
  project: string;
  organization: string;
  crops: CropsEnum[];
  status?: PlotStatusEnum;
};
export type PostPlotSuccessDto = {
  id: string;
  name: string;
  type?: PlotType;
  area: number;
  multi_polygon: MultiPolygon;
  project?: string | null;
  organization: string;
};
export type PostPlotErrorDto = Error;

/**
 * PATCH /api/v1/plot/{id}
 */
export const PATCH_PLOT_PATH = 'plot/{id}';
export type PatchPlotRequestPathVariables = { id: string };
export type PatchPlotRequestQueryParams = undefined;
export type PatchPlotRequestBodyData = Partial<{
  name: string;
  type: PlotType;
  project: string;
  crops: CropsEnum[];
  organization: string;
  status?: PlotStatusEnum;
  multi_polygon: MultiPolygon;
}>;
export type PatchPlotSuccessDto = {
  id: string;
  name: string;
  type?: PlotType;
  area: number;
  multi_polygon: MultiPolygon;
  project?: string | null;
  crops: CropsEnum[];
  organization: string;
};
export type PatchPlotErrorDto = Error;

/**
 * DELETE /api/v1/plot/{id}
 */
export const DELETE_PLOT_PATH = 'plot/{id}';
export type DeletePlotRequestPathVariables = {
  id: string;
};
export type DeletePlotRequestQueryParams = undefined;
export type DeletePlotSuccessDto = {
  /* no body */
};
export type DeletePlotErrorDto = Error;

/**
 * PATCH /api/v1/plot/{id}/get_questionnaire
 */
export const GET_PLOT_QUESTIONNAIRE_PATH = 'plot/{id}/get_questionnaire';
export type GetPlotQuestionnaireRequestPathVariables = { id: string };
export type GetPlotQuestionnaireRequestQueryParams = undefined;
export type GetPlotQuestionnaireRequestBodyData = undefined;
export type GetPlotQuestionnaireSuccessDto = { json: LandQuestionnaireAnswers<unknown> };
export type GetPlotQuestionnaireErrorDto = Error;

/**
 * PATCH /api/v1/plot/{id}/update_questionnaire
 */
export const PATCH_PLOT_QUESTIONNAIRE_PATH = 'plot/{id}/update_questionnaire';
export type PatchPlotQuestionnaireRequestPathVariables = { id: string };
export type PatchPlotQuestionnaireRequestQueryParams = undefined;
export type PatchPlotQuestionnaireRequestBodyData = {
  json: LandQuestionnaireAnswers<unknown>;
};
export type PatchPlotQuestionnaireSuccessDto = {
  /* no body */
};
export type PatchPlotQuestionnaireErrorDto = Error;

/**
 * POST /api/v1/plot/validate_multi_polygon
 */
export const POST_PLOT_VALIDATE_MULTI_POLYGON_PATH = 'plot/validate_multi_polygon';
export type PostPlotValidateMultiPolygonRequestPathVariables = undefined;
export type PostPlotValidateMultiPolygonRequestQueryParams = undefined;
export type PostPlotValidateMultiPolygonRequestBodyData = {
  multi_polygon: MultiPolygon;
};
export type PostPlotValidateMultiPolygonSuccessDto = {
  multi_polygon: MultiPolygon;
  centroid: Point;
  place_name: string;
  country: string;
};
export type PostPlotValidateMultiPolygonErrorDto = Error;

/**
 * GET /api/v1/buyer/plot
 */
export const GET_BUYER_PLOTS_PATH = 'buyer/plot';
export type GetBuyerPlotsRequestPathVariables = undefined;
export type GetBuyerPlotsRequestQueryParams = { project?: string };
export type GetBuyerPlotsSuccessDto = {
  count: number;
  next: null;
  previous: null;
  results: Plot[];
};
export type GetBuyerPlotsErrorDto = Error;

/**
 * GET /api/v1/buyer/plot/{id}
 */
export const GET_BUYER_PLOT_BY_ID_PATH = 'buyer/plot/{id}';
export type GetBuyerPlotByIdRequestPathVariables = { id: string };
export type GetBuyerPlotByIdRequestQueryParams = undefined;
export type GetBuyerPlotByIdSuccessDto = Plot;
export type GetBuyerPlotByIdErrorDto = Error;

/**
 * GET /api/v1/plots
 */
export const GET_PLOTS_PATH = 'plots';

export type GetPlotsRequestQueryParams =
  | {
      project?: string;
      demo?: boolean;
      workstream_id?: string;
    }
  | undefined;
export type GetPlotsSuccessDto = {
  count: number;
  next: string | null;
  previous: string | null;
  asset_column_name: string;
  results: PlotWithFacts[];
};
export type GetPlotsErrorDto = Error;

/**
 * GET /api/v1/plots/available-filters
 */
export const GET_AVAILABLE_FILTERS_PATH = 'plots/available-filters';
export type GetAvailableFiltersRequestQueryParams = { organization_id: string; workstream_id?: string };
export type GetAvailableFiltersSuccessDto = {
  available_countries: string[];
  available_country_codes: string[];
  available_plot_statuses: PlotStatusEnum[];
  available_crops: CropsEnum[];
  available_land_types: PlotType[];
  has_deforestation: boolean;
};
export type GetAvailableFiltersErrorDto = Error;

/**
 * GET /api/v1/plot-overview
 */
export const GET_PLOT_OVERVIEW_PATH = 'plot-overview';
export type GetPlotOverviewRequestPathVariables = undefined;
export type GetPlotOverviewRequestQueryParams =
  | {
      plot_id: string;
      is_approved?: boolean;
      are_media_files_approved?: boolean;
    }
  | undefined;
export type GetPlotOverviewSuccessDto = PlotOverview[];
export type GetPlotOverviewErrorDto = Error;

/**
 * POST /api/v1/plot-overview
 */
export const POST_PLOT_OVERVIEW_PATH = 'plot-overview';
export type PostPlotOverviewRequestPathVariables = undefined;
export type PostPlotOverviewRequestQueryParams = undefined;
export type PostPlotOverviewRequestBodyData = Partial<PlotOverview>;
export type PostPlotOverviewSuccessDto = PlotOverview;
export type PostPlotOverviewErrorDto = Error;

/**
 * PATCH /api/v1/plot-overview/{id}
 */
export const PATCH_PLOT_OVERVIEW_PATH = 'plot-overview/{id}';
export type PatchPlotOverviewRequestPathVariables = {
  id: string;
};
export type PatchPlotOverviewRequestQueryParams = undefined;
export type PatchPlotOverviewRequestBodyData = {
  title?: string;
  description?: string;
};
export type PatchPlotOverviewSuccessDto = PlotOverview;
export type PatchPlotOverviewErrorDto = Error;

/**
 * DELETE /api/v1/plot-overview/{id}
 */
export const DELETE_PLOT_OVERVIEW_PATH = 'plot-overview/{id}';
export type DeletePlotOverviewRequestPathVariables = {
  id: string;
};
export type DeletePlotOverviewRequestQueryParams = undefined;
export type DeletePlotOverviewSuccessDto = PlotOverview;
export type DeletePlotOverviewErrorDto = Error;

/**
 * POST /api/v1/plot-overview-media-file
 */
export const POST_PLOT_OVERVIEW_MEDIA_FILE_PATH = 'plot-overview-media-file';
export type PostPlotOverviewMediaFileRequestPathVariables = undefined;
export type PostPlotOverviewMediaFileRequestQueryParams = undefined;
export type PostPlotOverviewMediaFileRequestBodyData = {
  plot_overview: string;
  name: string;
  caption?: string;
  type: PlotOverviewMediaFileType;
  file_info: string;
  thumbnail_file_info?: string;
};
export type PostPlotOverviewMediaFileSuccessDto = PlotOverviewMediaFile;
export type PostPlotOverviewMediaFileErrorDto = Error;

/**
 * PATCH /api/v1/plot-overview-media-file/{id}
 */
export const PATCH_PLOT_OVERVIEW_MEDIA_FILE_PATH = 'plot-overview-media-file/{id}';
export type PatchPlotOverviewMediaFileRequestPathVariables = {
  id: string;
};
export type PatchPlotOverviewMediaFileRequestQueryParams = undefined;
export type PatchPlotOverviewMediaFileRequestBodyData = {
  name?: string;
  caption?: string;
  thumbnail_file_info?: string;
};
export type PatchPlotOverviewMediaFileSuccessDto = PlotOverviewMediaFile;
export type PatchPlotOverviewMediaFileErrorDto = Error;

/**
 * DELETE /api/v1/plot-overview-media-file/{id}
 */
export const DELETE_PLOT_OVERVIEW_MEDIA_FILE_PATH = 'plot-overview-media-file/{id}';
export type DeletePlotOverviewMediaFileRequestPathVariables = {
  id: string;
};
export type DeletePlotOverviewMediaFileRequestQueryParams = undefined;
export type DeletePlotOverviewMediaFileSuccessDto = PlotOverviewMediaFile;
export type DeletePlotOverviewMediaFileErrorDto = Error;
